import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Shield, Mail, Lock, User, Building, Eye, EyeOff, CheckCircle, Gift } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { LogoIcon } from '../components/Logo';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Password requirements for validation
const PASSWORD_REQUIREMENTS = [
  { id: 'length', label: 'At least 8 characters', test: (p) => p.length >= 8 },
  { id: 'uppercase', label: 'One uppercase letter', test: (p) => /[A-Z]/.test(p) },
  { id: 'lowercase', label: 'One lowercase letter', test: (p) => /[a-z]/.test(p) },
  { id: 'number', label: 'One number', test: (p) => /\d/.test(p) },
  { id: 'special', label: 'One special character (!@#$%^&*)', test: (p) => /[!@#$%^&*(),.?":{}|<>]/.test(p) },
];

export default function Signup() {
  const [companyName, setCompanyName] = useState('');
  const [adminName, setAdminName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPasswordReqs, setShowPasswordReqs] = useState(false);
  const [referralInfo, setReferralInfo] = useState(null);
  const { setAuthToken } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Get referral code from URL
  const referralCode = searchParams.get('ref');

  // Validate referral code on mount
  useEffect(() => {
    if (referralCode) {
      validateReferralCode(referralCode);
    }
  }, [referralCode]);

  const validateReferralCode = async (code) => {
    try {
      const response = await axios.get(`${API}/api/referrals/validate/${code}`);
      if (response.data.valid) {
        setReferralInfo(response.data);
      }
    } catch (error) {
      console.error('Failed to validate referral code:', error);
    }
  };

  // Check if all password requirements are met
  const allRequirementsMet = PASSWORD_REQUIREMENTS.every(req => req.test(password));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!agreedToTerms) {
      toast.error('Please agree to the Terms of Service and Privacy Policy');
      return;
    }
    
    // Validate password requirements
    if (!allRequirementsMet) {
      toast.error('Password does not meet security requirements');
      return;
    }
    
    setLoading(true);
    try {
      // Use the new signup API with email verification
      const response = await axios.post(`${API}/api/auth/signup`, {
        company_name: companyName,
        admin_name: adminName,
        admin_email: email,
        admin_password: password,
        referral_code: referralCode || null
      });
      
      // Check if verification is required
      if (response.data.requires_verification) {
        let message = 'Account created! Please check your email to verify your account.';
        if (response.data.referral_applied) {
          message = 'Account created with 1 month free credit! Please check your email to verify your account.';
        }
        toast.success(message);
        // Redirect to verification pending page
        navigate(`/verify-email?email=${encodeURIComponent(email)}`);
      } else {
        // Direct login (shouldn't happen with new flow)
        if (response.data.access_token) {
          sessionStorage.setItem('onboarding_token', response.data.access_token);
          toast.success('Account created! Let\'s complete your setup.');
          navigate('/onboarding?step=1');
        }
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (typeof detail === 'object' && detail.errors) {
        // Password validation errors
        toast.error(detail.errors[0]);
      } else {
        toast.error(detail || 'Signup failed');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex" data-testid="signup-page">
      {/* Left - Signup Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <Card className="w-full max-w-md bg-slate-900 border-slate-800">
          <CardHeader className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center">
              <LogoIcon size="xl" />
            </div>
            <CardTitle className="text-2xl font-heading text-white">Get Started</CardTitle>
            <CardDescription className="text-slate-400">
              Create your Clear Defense AI account
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Referral Banner */}
            {referralInfo && (
              <div className="mb-6 p-4 rounded-lg bg-gradient-to-r from-purple-500/10 to-blue-500/10 border border-purple-500/30">
                <div className="flex items-center gap-2 mb-2">
                  <Gift className="w-5 h-5 text-purple-400" />
                  <span className="font-semibold text-white">Referral Applied!</span>
                </div>
                <p className="text-sm text-slate-300">
                  Referred by <span className="text-purple-400 font-medium">{referralInfo.referrer_company_name}</span>
                </p>
                <p className="text-xs text-emerald-400 mt-1">
                  <CheckCircle className="w-3 h-3 inline mr-1" />
                  You&apos;ll receive 1 month free when you sign up!
                </p>
              </div>
            )}
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="company" className="text-slate-300">Company Name</Label>
                <div className="relative">
                  <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <Input
                    id="company"
                    type="text"
                    placeholder="Acme Corp"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    className="pl-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                    required
                    data-testid="signup-company"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="name" className="text-slate-300">Your Name</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <Input
                    id="name"
                    type="text"
                    placeholder="John Doe"
                    value={adminName}
                    onChange={(e) => setAdminName(e.target.value)}
                    className="pl-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                    required
                    data-testid="signup-name"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-300">Work Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                    required
                    data-testid="signup-email"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-300">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onFocus={() => setShowPasswordReqs(true)}
                    className="pl-10 pr-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                    required
                    data-testid="signup-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                
                {/* Password Requirements */}
                {showPasswordReqs && password && (
                  <div className="bg-slate-800/50 rounded-lg p-3 mt-2 space-y-1.5">
                    {PASSWORD_REQUIREMENTS.map((req) => (
                      <div key={req.id} className="flex items-center gap-2">
                        <div className={`w-4 h-4 rounded-full flex items-center justify-center ${
                          req.test(password) ? 'bg-emerald-500/20' : 'bg-slate-700'
                        }`}>
                          {req.test(password) && (
                            <CheckCircle className="w-3 h-3 text-emerald-500" />
                          )}
                        </div>
                        <span className={`text-xs ${
                          req.test(password) ? 'text-emerald-400' : 'text-slate-500'
                        }`}>
                          {req.label}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex items-start space-x-3">
                <Checkbox
                  id="terms"
                  checked={agreedToTerms}
                  onCheckedChange={(checked) => setAgreedToTerms(checked)}
                  className="mt-1 border-slate-600 data-[state=checked]:bg-blue-600"
                />
                <label htmlFor="terms" className="text-xs text-slate-400 cursor-pointer">
                  I agree to the{' '}
                  <Link to="/terms" className="text-blue-400 hover:text-blue-300 underline" target="_blank">
                    Terms of Service
                  </Link>{' '}
                  and{' '}
                  <Link to="/privacy" className="text-blue-400 hover:text-blue-300 underline" target="_blank">
                    Privacy Policy
                  </Link>
                </label>
              </div>
              <Button
                type="submit"
                className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                disabled={loading || !agreedToTerms || !allRequirementsMet}
                data-testid="signup-submit"
              >
                {loading ? 'Creating account...' : 'Create Account'}
              </Button>
            </form>
            <div className="mt-6 text-center">
              <p className="text-slate-400">
                Already have an account?{' '}
                <Link to="/login" className="text-blue-500 hover:text-blue-400" data-testid="login-link">
                  Sign in
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Right - Hero Image */}
      <div 
        className="hidden lg:flex flex-1 items-center justify-center p-12"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1660836814985-8523a0d713b5?crop=entropy&cs=srgb&fm=jpg&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="glass rounded-2xl p-8 max-w-md text-center">
          <h2 className="font-heading font-bold text-3xl text-white mb-4">
            Deploy in Minutes
          </h2>
          <p className="text-slate-300">
            Start protecting your organization immediately with our cloud-based cybersecurity platform. No complex setup required.
          </p>
          <div className="mt-6 flex justify-center gap-4">
            <div className="text-center">
              <div className="text-2xl font-heading font-bold text-blue-500">5</div>
              <div className="text-xs text-slate-400">Seats Included</div>
            </div>
            <div className="w-px bg-slate-700" />
            <div className="text-center">
              <div className="text-2xl font-heading font-bold text-blue-500">4</div>
              <div className="text-xs text-slate-400">Protection Layers</div>
            </div>
            <div className="w-px bg-slate-700" />
            <div className="text-center">
              <div className="text-2xl font-heading font-bold text-blue-500">AI</div>
              <div className="text-xs text-slate-400">Threat Engine</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
